// FE mirror of the backend path matrix (`BaseNodeV2.path_keys` /
// `handle_for_path` / `compute_path_keys` in spotflow/nodes/base_v2.py —
// change them together). A multi-path node declares per-path values in its
// `path_values` config; the engine projects each row onto the items that
// took its path as `<node>.<path>.<name>`, aligned with the node's outputs
// (Skipped at the positions that went elsewhere).

import type { NodeDefinition } from "@/generated";
import { getOutputHandles } from "@/store/editor-v2";
import {
  dynamicHandlesFromConfig,
  isDynamicHandleNode,
} from "./smartRouteBindings";

export const ON_SUCCESS_PATH_KEY = "on_success";
export const ON_ERROR_PATH_KEY = "on_error";

export interface PathInfo {
  key: string;
  // The edge handle the path gates through: `on_success` rides the generic
  // `output` handle; every other key is its own handle.
  handleId: string;
  label: string;
}

export interface PathValueRow {
  name: string;
  value: string;
}

export function handleForPath(pathKey: string): string {
  return pathKey === ON_SUCCESS_PATH_KEY ? "output" : pathKey;
}

function nodeSupportsErrorHandling(def: NodeDefinition | undefined): boolean {
  return def?.supports_error_handling !== false;
}

/** The node's current output paths, in display order. Routing nodes list
 * their branches (+ On error under the skip action — never On success, the
 * branches ARE the success paths); non-routing skip nodes get On success +
 * On error; plain nodes have none. */
export function pathKeysFor(
  nodeType: string | undefined,
  registryEntry: NodeDefinition | undefined,
  nodeConfig: Record<string, unknown> | undefined
): PathInfo[] {
  const routing: PathInfo[] = isDynamicHandleNode(nodeType)
    ? dynamicHandlesFromConfig(nodeType, nodeConfig).map((h) => ({
        key: h.id,
        handleId: h.id,
        label: h.label,
      }))
    : (registryEntry ? getOutputHandles(registryEntry) : []).map((h) => ({
        key: h,
        handleId: h,
        label: h.replace(/_/g, " "),
      }));
  const skip =
    nodeConfig?.on_error_action === "skip" &&
    nodeSupportsErrorHandling(registryEntry);
  const onError: PathInfo = {
    key: ON_ERROR_PATH_KEY,
    handleId: ON_ERROR_PATH_KEY,
    label: "On error",
  };
  if (routing.length > 0) return skip ? [...routing, onError] : routing;
  if (!skip) return [];
  return [
    { key: ON_SUCCESS_PATH_KEY, handleId: "output", label: "On success" },
    onError,
  ];
}

/** Tolerant read of a node's `path_values` config. */
export function pathRowsFor(
  nodeConfig: Record<string, unknown> | undefined
): Record<string, PathValueRow[]> {
  const raw = nodeConfig?.path_values;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: Record<string, PathValueRow[]> = {};
  for (const [path, rows] of Object.entries(raw as Record<string, unknown>)) {
    if (!Array.isArray(rows)) continue;
    const clean: PathValueRow[] = [];
    for (const row of rows) {
      if (!row || typeof row !== "object" || Array.isArray(row)) continue;
      const { name, value } = row as { name?: unknown; value?: unknown };
      clean.push({
        name: typeof name === "string" ? name : "",
        value: typeof value === "string" ? value : "",
      });
    }
    out[path] = clean;
  }
  return out;
}

/** First-dot split of a pool output key into a path interpretation, or null
 * when it has no dot. Callers must confirm `pathKey` against the source's
 * actual paths — an exact (dotted) real output name always wins. */
export function splitPathOutputKey(
  outputName: string
): { pathKey: string; name: string } | null {
  const dot = outputName.indexOf(".");
  if (dot <= 0 || dot === outputName.length - 1) return null;
  return { pathKey: outputName.slice(0, dot), name: outputName.slice(dot + 1) };
}

export function formatPathVarLabel(pathLabel: string, name: string): string {
  return `${pathLabel} · ${name}`;
}

/** The picker's label for a `<pathKey>.<name>` output key of this node, or
 * undefined when the key's prefix isn't one of the node's current paths. */
export function pathVarLabel(
  nodeType: string | undefined,
  registryEntry: NodeDefinition | undefined,
  nodeConfig: Record<string, unknown> | undefined,
  outputName: string
): string | undefined {
  const split = splitPathOutputKey(outputName);
  if (!split) return undefined;
  const info = pathKeysFor(nodeType, registryEntry, nodeConfig).find(
    (p) => p.key === split.pathKey
  );
  return info ? formatPathVarLabel(info.label, split.name) : undefined;
}

/** The declared row backing `<pathKey>.<name>` on a source node, if any. */
export function declaredPathVar(
  nodeConfig: Record<string, unknown> | undefined,
  pathKey: string,
  name: string
): PathValueRow | undefined {
  return pathRowsFor(nodeConfig)[pathKey]?.find(
    (row) => row.name === name && row.name.length > 0
  );
}
